import { ListBulletsIcon } from '@phosphor-icons/react';
import {
  Alert,
  Badge,
  Button,
  Cell,
  Column,
  EmptyState,
  Pagination,
  Row,
  SegmentedTabs,
  Table,
  TableBody,
  TableContainer,
  TableHeader,
} from '@skupreme/shared/ui';
import { ToastMessage } from '@utility/toast';
import { type ReactNode, useState } from 'react';
import { useListingMapping } from '../hooks/use-listing-mapping';
import { useMappableListings } from '../hooks/use-mappable-listings';
import { mappingErrorMessage } from '../lib/mapping-errors';
import {
  ALL_SOURCES,
  EXCLUDED_REASON,
  excludeBody,
  mapBody,
  type SourceFilter,
} from '../lib/mappable-params';
import type {
  ListingMappingBody,
  ListingMappingResult,
  MappableListing,
  MappingState,
} from '../lib/types';
import { CreateItemModal } from './CreateItemModal';
import { ExcludeListingModal } from './ExcludeListingModal';
import { MappingActions, showInactiveFilter } from './MappingActions';
import { PostgresItemPickerModal } from './PostgresItemPickerModal';

export type MappableListingsTableProps = {
  state: MappingState;
  /** The page behind each tab; the tabs hand the choice back. */
  onStateChange: (state: MappingState) => void;
  initialSearch?: string;
  /** The plan or role can't change listings: actions show, disabled. */
  isReadOnly?: boolean;
  /** The plan hides these actions altogether. */
  isActionsHidden?: boolean;
  /** For a page that has its own tabs for the two states. */
  hideStateTabs?: boolean;
  /** An extra action on each row, e.g. Undo, before the table's own. */
  renderRowAction?: (listing: MappableListing) => ReactNode;
};

export const STORAGE_PROVIDER_LABEL = 'Storage provider';

const LOADING_ROWS = 7;
const COLUMN_COUNT = 6;

const STATE_TABS: { id: MappingState; label: string }[] = [
  { id: 'unmapped', label: 'Unmapped' },
  { id: 'mapped', label: 'Mapped' },
];

const SOURCE_OPTIONS: { value: SourceFilter; label: string }[] = [
  { value: ALL_SOURCES, label: 'All sources' },
  { value: 'marketplace', label: 'Marketplaces' },
  { value: 'storage_provider', label: 'Storage providers' },
];

const EMPTY_STATES: Record<
  MappingState,
  { title: string; description: string }
> = {
  unmapped: {
    title: 'No unmapped listings',
    description: 'Every listing in this view is mapped to a catalog item.',
  },
  mapped: {
    title: 'No mapped listings',
    description: 'Listings mapped to a catalog item show here.',
  },
};

/* Why a listing has no item yet, in words; an unknown reason shows as the API sent it. */
const UNMAPPED_REASONS: Record<string, string> = {
  no_candidate: 'No matching item',
  ambiguous: 'Several matching items',
  no_identifiers: 'No identifiers',
  gtin_conflict: 'GTIN conflict',
  pack_unknown: 'Pack size unknown',
  [EXCLUDED_REASON]: 'Excluded',
};

const SAVED_MESSAGES: Record<ListingMappingResult['status'], string> = {
  mapped: 'Listing mapped successfully',
  unchanged: 'Listing was already mapped to that item',
  excluded: 'Listing excluded successfully',
};

/* An excluded listing is unmapped on purpose; it is mapped again, not made into an item. */
const canCreateItem = (listing: MappableListing) =>
  listing.state === 'unmapped' && listing.unmappedReason !== EXCLUDED_REASON;

function StateCell({ listing }: { listing: MappableListing }) {
  if (listing.state === 'mapped') {
    return <span className="sk:truncate sk:text-sm">{listing.itemTitle}</span>;
  }

  const reason = listing.unmappedReason;

  return (
    <Badge tone={reason === EXCLUDED_REASON ? 'neutral' : 'caution'}>
      {UNMAPPED_REASONS[reason ?? ''] ?? reason ?? 'Unmapped'}
    </Badge>
  );
}

function Identifiers({ listing }: { listing: MappableListing }) {
  return (
    <div className="sk:flex sk:flex-col sk:text-dense sk:text-fg-secondary">
      {listing.gtin ? <span>GTIN {listing.gtin}</span> : null}
      {listing.asin ? <span>ASIN {listing.asin}</span> : null}
    </div>
  );
}

/**
 * Listings of every connection of the account, marketplaces and storage
 * providers alike, to map to a catalog item, re-map or exclude. Reads and
 * writes Postgres only.
 */
export function MappableListingsTable({
  state,
  onStateChange,
  initialSearch,
  isReadOnly,
  isActionsHidden,
  hideStateTabs,
  renderRowAction,
}: MappableListingsTableProps) {
  const { data, loading, error, totalData, filter, filterChanged } =
    useMappableListings(state, initialSearch);
  const mapping = useListingMapping();
  const [picking, setPicking] = useState<MappableListing | null>(null);
  const [excluding, setExcluding] = useState<MappableListing | null>(null);
  const [creating, setCreating] = useState<MappableListing | null>(null);

  const save = async (listing: MappableListing, body: ListingMappingBody) => {
    try {
      const result = await mapping.mutateAsync({ listingId: listing.id, body });
      ToastMessage.success(SAVED_MESSAGES[result.status]);
      setPicking(null);
      setExcluding(null);
    } catch (failure) {
      ToastMessage.error(mappingErrorMessage(failure));
    }
  };

  const locked = isReadOnly || mapping.isPending;
  const empty = EMPTY_STATES[state];

  return (
    <div className="sk:flex sk:flex-col sk:gap-3">
      <div className="sk:flex sk:flex-wrap sk:items-center sk:gap-3">
        {hideStateTabs ? null : (
          <SegmentedTabs
            label="Mapping state"
            items={STATE_TABS}
            selectedKey={state}
            onSelectionChange={key => onStateChange(key as MappingState)}
          />
        )}
        <MappingActions
          filters={[
            {
              id: 'source',
              label: 'Source',
              value: filter.source,
              options: SOURCE_OPTIONS,
              onChange: source =>
                filterChanged('source', source as SourceFilter),
            },
            /* Only unmapped listings are left out by status (M2). */
            ...(state === 'unmapped'
              ? [
                  showInactiveFilter(filter.showInactive, isOn =>
                    filterChanged('showInactive', isOn),
                  ),
                ]
              : []),
          ]}
          search={filter.search}
          onSearch={search => filterChanged('search', search)}
        />
      </div>
      {error ? <Alert tone="danger">{mappingErrorMessage(error)}</Alert> : null}
      <TableContainer className="sk:overflow-visible sk:rounded-card sk:bg-surface">
        <Table aria-label="Listings" className="sk:table-fixed">
          <TableHeader>
            <Column id="source" isRowHeader className="sk:w-[18%]">
              Source
            </Column>
            <Column id="sku" className="sk:w-[12%]">
              SKU
            </Column>
            <Column id="title" className="sk:w-[26%]">
              Title
            </Column>
            <Column id="identifiers" className="sk:w-[14%]">
              GTIN / ASIN
            </Column>
            <Column id="state" className="sk:w-[16%]">
              {state === 'mapped' ? 'Mapped item' : 'State'}
            </Column>
            <Column id="actions" aria-label="Actions" className="sk:w-[14%]">
              {''}
            </Column>
          </TableHeader>
          <TableBody
            renderEmptyState={() => (
              <EmptyState
                icon={<ListBulletsIcon size={20} />}
                title={empty.title}
                description={empty.description}
              />
            )}
          >
            {loading
              ? Array.from({ length: LOADING_ROWS }, (_, row) => (
                  <Row key={`placeholder-${row}`} id={`placeholder-${row}`}>
                    <Cell colSpan={COLUMN_COUNT}>
                      <div className="sk:h-10 sk:animate-pulse sk:rounded-option sk:bg-fill-tertiary" />
                    </Cell>
                  </Row>
                ))
              : data.map(listing => (
                  <Row key={listing.id} id={listing.id}>
                    <Cell>
                      <div className="sk:flex sk:min-w-0 sk:flex-col sk:items-start sk:gap-1">
                        <span className="sk:truncate sk:text-sm sk:text-fg">
                          {listing.connectionLabel ?? 'Unknown connection'}
                        </span>
                        {listing.sourceKind === 'storage_provider' ? (
                          <Badge tone="info">{STORAGE_PROVIDER_LABEL}</Badge>
                        ) : null}
                      </div>
                    </Cell>
                    <Cell>{listing.sku}</Cell>
                    <Cell>
                      <span className="sk:truncate sk:text-sm">
                        {listing.title}
                      </span>
                    </Cell>
                    <Cell>
                      <Identifiers listing={listing} />
                    </Cell>
                    <Cell>
                      <StateCell listing={listing} />
                    </Cell>
                    <Cell>
                      {isActionsHidden ? null : (
                        <div className="sk:flex sk:flex-wrap sk:items-center sk:justify-end sk:gap-2">
                          {renderRowAction?.(listing)}
                          <Button
                            variant="primary"
                            size="sm"
                            isDisabled={locked}
                            onPress={() => setPicking(listing)}
                          >
                            {listing.state === 'mapped' ? 'Re-map' : 'Map'}
                          </Button>
                          {canCreateItem(listing) && !isReadOnly ? (
                            <Button
                              variant="ghost"
                              size="sm"
                              isDisabled={locked}
                              onPress={() => setCreating(listing)}
                            >
                              Create item
                            </Button>
                          ) : null}
                          {listing.unmappedReason === EXCLUDED_REASON ? null : (
                            <Button
                              variant="ghost"
                              size="sm"
                              isDisabled={locked}
                              onPress={() => setExcluding(listing)}
                            >
                              Exclude
                            </Button>
                          )}
                        </div>
                      )}
                    </Cell>
                  </Row>
                ))}
          </TableBody>
        </Table>
        {totalData > 0 && (
          <Pagination
            page={filter.page}
            pageSize={filter.limit}
            total={totalData}
            onPageChange={page => {
              filterChanged('page', page);
              window.scrollTo({ top: 0 });
            }}
            onPageSizeChange={limit => filterChanged('limit', limit)}
          />
        )}
      </TableContainer>
      <PostgresItemPickerModal
        listing={picking}
        onClose={() => setPicking(null)}
        onSelect={itemId => picking && save(picking, mapBody(picking, itemId))}
        isProcessing={mapping.isPending}
      />
      <CreateItemModal listing={creating} onClose={() => setCreating(null)} />
      <ExcludeListingModal
        listing={excluding}
        onClose={() => setExcluding(null)}
        onConfirm={() => excluding && save(excluding, excludeBody)}
        isProcessing={mapping.isPending}
      />
    </div>
  );
}
